import { useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useLocation } from 'react-router-dom'
import { Loading } from '@winnin/winnin_ui.components.loading'
import { Title } from '@winnin/winnin_ui.components.title'

import LoadingErrorWrapper from 'components/LoadingErrorWrapper'
import SeeMoreCard from 'components/SeeMoreCard'
import SkeletonTable from 'components/SkeletonTable'
import { TerritoriesTable } from 'components/TerritoriesTable'
import TerritoryChipsFilter from 'components/TerritoryChipsFilter'
import getTerritoryFiltersFromUrl from 'components/TerritoryChipsFilter/utils/getTerritoryFiltersFromUrl'
import { ListTerrotoriesSortFields, ListTerrotoriesSortOrder } from 'generated/graphql'
import { T, useTranslate } from 'lib/tolgee'
import useGetUnifiedTerritories from 'modules/territory/common/hooks/use-get-unified-territories'
import type { OrderOption } from 'pages/SearchTerritoriesPage/utils/getVariables'
import { getVariables } from 'pages/SearchTerritoriesPage/utils/getVariables'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import ButtonSearchVideos from '../ButtonSearchVideos'
import TerritoriesInSearchList from '../TerritoriesInSearchList'

import './index.scss'

interface IRelatedTerritoriesView {
  search: string
}

const { TOP, SORT } = analyticsTracker.eventNames

export default function RelatedTerritoriesView({ search }: IRelatedTerritoriesView) {
  const [isSeeLess, setIsSeeLess] = useState(false)
  const [order, setOrder] = useState<OrderOption>({
    field: ListTerrotoriesSortFields.AvgEngagement,
    order: ListTerrotoriesSortOrder.Desc,
  })

  const t = useTranslate()
  const location = useLocation()

  const filters = useMemo(() => {
    return getTerritoryFiltersFromUrl(location.search)
  }, [location.search])

  const variables = useMemo(() => {
    return getVariables(filters, search, order, 4)
  }, [filters, order, search])

  const { data, error, fetchMore, isLoading, refetch, hasMore, totalCount, isFetchingMore, wasCalled } =
    useGetUnifiedTerritories({
      variables,
      audience: filters?.audience,
    })

  const handleSetOrder = field => {
    setOrder(prevState => {
      const previousOrder = prevState.order === 'desc' ? ListTerrotoriesSortOrder.Asc : ListTerrotoriesSortOrder.Desc
      const newOrder = {
        field,
        order: prevState.field === field ? previousOrder : ListTerrotoriesSortOrder.Desc,
      }

      analyticsTracker.eventTrack(SORT, {
        Page: 'Search Topics Page',
        'Sort field': field,
        'Sort order': newOrder.order,
      })

      return newOrder
    })
  }

  const handleSeeMore = () => {
    fetchMore()

    analyticsTracker.eventTrack(TOP.SEE_MORE_TOPICS, {
      searchTerm: search,
    })
  }

  const handleSeeMode = () => {
    setIsSeeLess(prevState => !prevState)
  }

  const finalTableData = useMemo(() => {
    if (isSeeLess) {
      return data.slice(0, variables.pagination.take)
    }
    return data
  }, [data, isSeeLess, variables.pagination.take])

  const foundFilter = useMemo(() => {
    return !data.find(item => item.name.toLowerCase() === search.toLowerCase()) && wasCalled
  }, [data, search, wasCalled])

  const notTable = useMemo(() => {
    return data?.length <= 0 && search
  }, [data, search])

  return (
    <div className="RelatedTerritoriesView-wrapper">
      {foundFilter && <ButtonSearchVideos searchFilter={search} />}
      <div>
        {isLoading ? (
          <div className="countSection">
            <Skeleton
              height={12}
              borderRadius={20}
              width={150}
              containerClassName="SkeletonTable-lines"
              baseColor="var(--color_neutral_400)"
            />
          </div>
        ) : (
          !notTable && (
            <Title tag="h3" className="countSection">
              <T
                keyName={TOLGEE_GLOBALS.relatedResults.key}
                parameters={{
                  results: totalCount ?? 0,
                }}
              >
                {`${TOLGEE_GLOBALS.relatedResults.defaultValue}`}
              </T>
            </Title>
          )
        )}
      </div>
      {!notTable && (
        <div className="filtersSection">
          <TerritoryChipsFilter shouldHideTypeFilter />
        </div>
      )}
      <div className="tableSection">
        <LoadingErrorWrapper
          error={error}
          loading={isLoading}
          refetch={refetch}
          isEmpty={!data?.length && !notTable}
          skeletonComponent={SkeletonTable}
        >
          {!notTable && (
            <>
              <TerritoriesTable
                territories={finalTableData}
                handleSort={handleSetOrder}
                sortOrder={order.order}
                sortField={order.field}
                allowSortByVideos
              />
              {isFetchingMore && <Loading />}
              {hasMore && !isFetchingMore && (
                <SeeMoreCard className="seeMoreCard" onClick={handleSeeMore} disabled={isFetchingMore} />
              )}
              {data?.length > variables.pagination.take && !hasMore && (
                <button onClick={handleSeeMode} className="toggleViewButton">
                  {isSeeLess ? t(TOLGEE_GLOBALS.seeMore) : t(TOLGEE_GLOBALS.seeLess)}
                </button>
              )}
            </>
          )}
        </LoadingErrorWrapper>
        <TerritoriesInSearchList searchTerm={search} />
      </div>
    </div>
  )
}
